import { create } from 'zustand';
import { createJSONStorage, persist, StateStorage } from 'zustand/middleware';
import { User } from '../types';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  mfaRequired: boolean;
  mfaToken: string | null;
  rememberMe: boolean;
  setAuth: (user: User, accessToken: string, refreshToken: string, rememberMe?: boolean) => void;
  setMfaRequired: (user: User, mfaToken: string) => void;
  clearMfa: () => void;
  updateAccessToken: (accessToken: string) => void;
  updateUser: (user: Partial<User>) => void;
  clearAuth: () => void;
  hasPermission: (resource: string, action: string) => boolean;
}

// "Remember me" unchecked should end the session when the browser closes, so those
// sessions live in sessionStorage instead of localStorage. The storage engine is
// chosen per-write by inspecting the rememberMe flag inside the serialized state.
const dynamicAuthStorage: StateStorage = {
  getItem: (name) => localStorage.getItem(name) ?? sessionStorage.getItem(name),
  setItem: (name, value) => {
    const rememberMe = JSON.parse(value)?.state?.rememberMe;
    if (rememberMe) {
      localStorage.setItem(name, value);
      sessionStorage.removeItem(name);
    } else {
      sessionStorage.setItem(name, value);
      localStorage.removeItem(name);
    }
  },
  removeItem: (name) => {
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
  },
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      mfaRequired: false,
      mfaToken: null,
      rememberMe: false,

      setAuth: (user, accessToken, refreshToken, rememberMe = false) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          mfaRequired: false,
          mfaToken: null,
          rememberMe,
        }),

      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),

      setMfaRequired: (user, mfaToken) =>
        set({
          user,
          mfaRequired: true,
          mfaToken,
          isAuthenticated: false,
        }),

      clearMfa: () =>
        set({
          mfaRequired: false,
          mfaToken: null,
        }),

      updateAccessToken: (accessToken) =>
        set({ accessToken }),

      clearAuth: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          mfaRequired: false,
          mfaToken: null,
          rememberMe: false,
        }),

      hasPermission: (resource, action) => {
        const state = get();
        if (!state.user || !state.user.role) return false;

        // Super admin bypasses all permission checks
        if (state.user.role.name === 'super_admin') return true;

        const permissions = state.user.role.permissions || [];
        return permissions.some(
          (p) => p.resource === resource && p.action === action
        );
      },
    }),
    {
      name: 'bb-auth-store', // key for localStorage/sessionStorage, chosen by rememberMe
      storage: createJSONStorage(() => dynamicAuthStorage),
    }
  )
);
